import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import type { PayGroupsLeftOut } from '@efacture/core';
import type { DecimalString } from '@efacture/web-billing/lib/decimal';

// ─────────────────────────────────────────────────────────────
// Types (miroir des modèles Prisma — les Decimal arrivent en string)
// Vague 1a Phase 2.x — fournisseurs & factures fournisseurs (journal AC)
// ─────────────────────────────────────────────────────────────

/** LOCAL → compte 4011, FOREIGN → compte 4013. */
export type SupplierType = 'LOCAL' | 'FOREIGN';
export type SupplierInvoiceStatus = 'DRAFT' | 'VALIDATED' | 'CANCELLED';
export type SupplierPaymentStatus = 'PENDING' | 'PARTIAL' | 'PAID';

export interface Supplier {
  id: string;
  name: string;
  supplierType: SupplierType;
  ifu: string | null;
  rccm: string | null;
  address: string | null;
  phone: string | null;
  email: string | null;
  notes: string | null;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupplierInvoiceLine {
  id: string;
  invoiceId: string;
  lineNumber: number;
  label: string;
  /** Compte de charge 6xx OU d'immobilisation 2xx. */
  accountNumber: string;
  amountHT: DecimalString;
  /** 0 | 10 | 18. */
  vatRate: number;
  vatAmount: DecimalString;
  /** Ligne 2xx → proposition de création d'immobilisation (vague 1b). */
  isFixedAsset: boolean;
  assetId: string | null;
}

export interface SupplierPayment {
  id: string;
  supplierInvoiceId: string;
  /** ESPECES | CHEQUE | VIREMENT | CARTE_BANCAIRE | MOBILE_MONEY. */
  method: string;
  amount: DecimalString;
  reference: string | null;
  paidAt: string;
  /** Its entry is contre-passée: it no longer counts in the amount paid. */
  cancelled: boolean;
}

export interface SupplierInvoice {
  id: string;
  supplierId: string;
  /** N° de la facture CHEZ le fournisseur (anti double-saisie). */
  supplierRef: string;
  /** ACH-YYYY-NNNNNN à la validation — jeton BROUILLON-… avant. */
  internalNumber: string;
  date: string;
  dueDate: string | null;
  totalHT: DecimalString;
  totalTVA: DecimalString;
  totalTTC: DecimalString;
  /** Currency the invoice is stated in: the currency of its amounts, lines and payments. */
  currency: string;
  status: SupplierInvoiceStatus;
  paymentStatus: SupplierPaymentStatus;
  amountPaid: DecimalString;
  notes: string | null;
  validatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lines: SupplierInvoiceLine[];
}

/** Élément de liste — fournisseur allégé inclus. */
export type SupplierInvoiceListItem = SupplierInvoice & {
  supplier: Pick<Supplier, 'id' | 'name' | 'supplierType'>;
};

/** Détail — fournisseur complet + règlements. */
export type SupplierInvoiceDetail = SupplierInvoice & {
  supplier: Supplier;
  payments: SupplierPayment[];
};

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

// ─────────────────────────────────────────────────────────────
// Libellés d'affichage (français — homologation DGI)
// ─────────────────────────────────────────────────────────────

export const SUPPLIER_TYPE_LABELS: Record<SupplierType, string> = {
  LOCAL: 'Local',
  FOREIGN: 'Étranger',
};

/** Compte fournisseur SYSCOHADA associé au type. */
export const SUPPLIER_TYPE_ACCOUNTS: Record<SupplierType, string> = {
  LOCAL: '4011',
  FOREIGN: '4013',
};

export const SUPPLIER_INVOICE_STATUS_LABELS: Record<SupplierInvoiceStatus, string> = {
  DRAFT: 'Brouillon',
  VALIDATED: 'Validée',
  CANCELLED: 'Annulée',
};

export const SUPPLIER_PAYMENT_STATUS_LABELS: Record<SupplierPaymentStatus, string> = {
  PENDING: 'En attente',
  PARTIAL: 'Partiel',
  PAID: 'Payée',
};

/** Moyens de paiement acceptés par POST /supplier-invoices/:id/payments. */
export const SUPPLIER_PAYMENT_METHOD_OPTIONS: { value: string; label: string }[] = [
  { value: 'ESPECES', label: 'Espèces' },
  { value: 'CHEQUE', label: 'Chèque' },
  { value: 'VIREMENT', label: 'Virement bancaire' },
  { value: 'CARTE_BANCAIRE', label: 'Carte bancaire' },
  { value: 'MOBILE_MONEY', label: 'Mobile Money' },
];

export const SUPPLIER_PAYMENT_METHOD_LABELS: Record<string, string> =
  Object.fromEntries(
    SUPPLIER_PAYMENT_METHOD_OPTIONS.map((option) => [option.value, option.label])
  );

/** Taux de TVA acceptés sur les achats (spec vague 1a). */
export const SUPPLIER_VAT_RATES = [18, 10, 0] as const;

// ─────────────────────────────────────────────────────────────
// Paramètres et corps de requêtes
// ─────────────────────────────────────────────────────────────

export interface SupplierListParams {
  search?: string | undefined;
  supplierType?: SupplierType | undefined;
  isActive?: boolean | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface SupplierInput {
  name: string;
  supplierType?: SupplierType;
  ifu?: string | null;
  rccm?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  notes?: string | null;
  isActive?: boolean;
}

export interface SupplierInvoiceLineInput {
  label: string;
  accountNumber: string;
  amountHT: number;
  vatRate: number;
  /** Si absent : round2(HT × taux / 100) côté serveur. Modifiable (facture papier). */
  vatAmount?: number;
}

export interface SupplierInvoiceInput {
  supplierId: string;
  supplierRef: string;
  date: string; // YYYY-MM-DD
  dueDate?: string;
  notes?: string;
  lines: SupplierInvoiceLineInput[];
}

export interface SupplierInvoiceListParams {
  status?: SupplierInvoiceStatus | undefined;
  paymentStatus?: SupplierPaymentStatus | undefined;
  supplierId?: string | undefined;
  search?: string | undefined;
  page?: number | undefined;
  limit?: number | undefined;
}

export interface SupplierPaymentInput {
  method: string;
  amount: number;
  reference?: string;
  paidAt?: string; // YYYY-MM-DD
}

// ─────────────────────────────────────────────────────────────
// Réponses (tableaux nommés contextuellement — pitfall CLAUDE.md)
// ─────────────────────────────────────────────────────────────

export interface SupplierListResponse {
  suppliers: Supplier[];
  pagination: PaginationInfo;
}

export interface SupplierInvoiceListResponse {
  supplierInvoices: SupplierInvoiceListItem[];
  pagination: PaginationInfo;
}

export interface DeleteSupplierResult {
  /** true = suppression réelle (aucune facture). */
  deleted: boolean;
  /** true = factures existantes → désactivation à la place. */
  deactivated: boolean;
}

export interface RecordSupplierPaymentResponse {
  supplierInvoice: SupplierInvoiceDetail;
  payment: SupplierPayment;
}

// ─────────────────────────────────────────────────────────────
// Balance âgée généralisée (side=CLIENT 411x / SUPPLIER 401x /
// EMPLOYEE 422x — retour UAT comptable §3 #6, grand livre auxiliaire
// personnel)
// ─────────────────────────────────────────────────────────────

export type AgedBalanceSide = 'CLIENT' | 'SUPPLIER' | 'EMPLOYEE';

/**
 * Date each line is aged from: « facture » = the invoice date carried by
 * an imported open item, else the entry date; « echeance » = its due date,
 * then its invoice date, then the entry date.
 */
export type AgedBalanceBasis = 'facture' | 'echeance';

export interface AgedBalanceSideBuckets {
  b0_30: number;
  b31_60: number;
  b61_90: number;
  b90plus: number;
  total: number;
}

export interface AgedBalanceSideRow extends AgedBalanceSideBuckets {
  /** Client.id / Supplier.id — null pour les lignes sans tiers rattaché. */
  thirdPartyId: string | null;
  thirdPartyName: string;
}

export interface AgedBalanceSideResponse {
  side: AgedBalanceSide;
  /** Date de référence effective (YYYY-MM-DD). */
  asOf: string;
  rows: AgedBalanceSideRow[];
  totals: AgedBalanceSideBuckets;
  /** Personnel side without payroll:read: no row, the totals only. */
  employeeDetailWithheld?: boolean;
  /** "PARTLY_LETTERED" when partly lettered pay entries are missing from `totals`. */
  payGroupsLeftOut?: PayGroupsLeftOut | null;
}

/**
 * Vue PAR COMPTE TIERS (comptes tiers, spec 2026-07-24 §5.5) — mêmes
 * tranches, clé de regroupement = AuxiliaryAccount (code + libellé).
 */
export interface AgedBalanceTiersRow extends AgedBalanceSideBuckets {
  /** AuxiliaryAccount.id — null pour la ligne « (Sans tiers rattaché) ». */
  auxiliaryAccountId: string | null;
  code: string | null;
  label: string;
  type: string | null;
}

export interface AgedBalanceTiersResponse {
  side: AgedBalanceSide;
  asOf: string;
  rows: AgedBalanceTiersRow[];
  totals: AgedBalanceSideBuckets;
  /** Personnel side without payroll:read: no row, the totals only. */
  employeeDetailWithheld?: boolean;
  /** "PARTLY_LETTERED" when partly lettered pay entries are missing from `totals`. */
  payGroupsLeftOut?: PayGroupsLeftOut | null;
}

// ─────────────────────────────────────────────────────────────
// Aide Decimal — les colonnes Decimal Prisma sont sérialisées en string
// ─────────────────────────────────────────────────────────────

export { toNum } from '@efacture/web-billing/lib/decimal';

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const supplierKeys = {
  all: ['accounting-suppliers'] as const,
  suppliers: () => [...supplierKeys.all, 'suppliers'] as const,
  supplierList: (params: SupplierListParams) =>
    [...supplierKeys.suppliers(), 'list', params] as const,
  supplierDetail: (id: string) => [...supplierKeys.suppliers(), 'detail', id] as const,
  invoices: () => [...supplierKeys.all, 'invoices'] as const,
  invoiceList: (params: SupplierInvoiceListParams) =>
    [...supplierKeys.invoices(), 'list', params] as const,
  invoiceDetail: (id: string) => [...supplierKeys.invoices(), 'detail', id] as const,
  agedBalance: (side: AgedBalanceSide, asOf?: string, basis: AgedBalanceBasis = 'facture') =>
    [...supplierKeys.all, 'aged-balance', side, asOf ?? 'today', basis] as const,
  agedBalanceTiers: (side: AgedBalanceSide, asOf?: string, basis: AgedBalanceBasis = 'facture') =>
    [...supplierKeys.all, 'aged-balance-tiers', side, asOf ?? 'today', basis] as const,
};

// ─────────────────────────────────────────────────────────────
// Fonctions API — fournisseurs
// ─────────────────────────────────────────────────────────────

/**
 * Exported below (not just used internally by `useSuppliers`) so pickers
 * can drive it directly as `Select`'s `loadOptions` — see
 * `ImportCostSheetDetailPage`'s supplier field.
 */
async function fetchSuppliers(params: SupplierListParams): Promise<SupplierListResponse> {
  return apiClient.get<SupplierListResponse>('/accounting/suppliers', {
    search: params.search || undefined,
    supplierType: params.supplierType,
    // boolean → 'true'/'false' dans la query string (schéma serveur)
    isActive: params.isActive === undefined ? undefined : params.isActive,
    page: params.page,
    limit: params.limit,
  });
}

/**
 * Exported below so callers can resolve a KNOWN id without going through
 * the paginated list (e.g. `useQueries` over several ids — see
 * `ImportCostSheetsPage`'s supplier-name column).
 */
async function fetchSupplier(id: string): Promise<Supplier> {
  const result = await apiClient.get<{ supplier: Supplier }>(
    `/accounting/suppliers/${id}`
  );
  return result.supplier;
}

async function createSupplier(data: SupplierInput): Promise<Supplier> {
  const result = await apiClient.post<{ supplier: Supplier }>(
    '/accounting/suppliers',
    data
  );
  return result.supplier;
}

async function updateSupplier(id: string, data: Partial<SupplierInput>): Promise<Supplier> {
  const result = await apiClient.put<{ supplier: Supplier }>(
    `/accounting/suppliers/${id}`,
    data
  );
  return result.supplier;
}

async function deleteSupplier(id: string): Promise<DeleteSupplierResult> {
  return apiClient.delete<DeleteSupplierResult>(`/accounting/suppliers/${id}`);
}

// ─────────────────────────────────────────────────────────────
// Fonctions API — factures fournisseurs
// ─────────────────────────────────────────────────────────────

async function fetchSupplierInvoices(
  params: SupplierInvoiceListParams
): Promise<SupplierInvoiceListResponse> {
  return apiClient.get<SupplierInvoiceListResponse>('/accounting/supplier-invoices', {
    status: params.status,
    paymentStatus: params.paymentStatus,
    supplierId: params.supplierId,
    search: params.search || undefined,
    page: params.page,
    limit: params.limit,
  });
}

async function fetchSupplierInvoice(id: string): Promise<SupplierInvoiceDetail> {
  const result = await apiClient.get<{ supplierInvoice: SupplierInvoiceDetail }>(
    `/accounting/supplier-invoices/${id}`
  );
  return result.supplierInvoice;
}

async function createSupplierInvoice(
  data: SupplierInvoiceInput
): Promise<SupplierInvoice> {
  const result = await apiClient.post<{ supplierInvoice: SupplierInvoice }>(
    '/accounting/supplier-invoices',
    data
  );
  return result.supplierInvoice;
}

async function updateSupplierInvoice(
  id: string,
  data: SupplierInvoiceInput
): Promise<SupplierInvoice> {
  const result = await apiClient.put<{ supplierInvoice: SupplierInvoice }>(
    `/accounting/supplier-invoices/${id}`,
    data
  );
  return result.supplierInvoice;
}

async function deleteSupplierInvoice(id: string): Promise<void> {
  await apiClient.delete(`/accounting/supplier-invoices/${id}`);
}

/** POST sans corps — l'apiClient n'envoie pas de Content-Type sans body. */
async function validateSupplierInvoice(id: string): Promise<SupplierInvoiceDetail> {
  const result = await apiClient.post<{ supplierInvoice: SupplierInvoiceDetail }>(
    `/accounting/supplier-invoices/${id}/validate`
  );
  return result.supplierInvoice;
}

async function recordSupplierPayment(
  id: string,
  data: SupplierPaymentInput
): Promise<RecordSupplierPaymentResponse> {
  return apiClient.post<RecordSupplierPaymentResponse>(
    `/accounting/supplier-invoices/${id}/payments`,
    data
  );
}

// ─────────────────────────────────────────────────────────────
// Fonction API — balance âgée (side-aware)
// ─────────────────────────────────────────────────────────────

async function fetchAgedBalanceSide(
  side: AgedBalanceSide,
  asOf?: string,
  basis: AgedBalanceBasis = 'facture'
): Promise<AgedBalanceSideResponse> {
  return apiClient.get<AgedBalanceSideResponse>('/accounting/aged-balance', {
    asOf: asOf || undefined,
    side,
    basis,
  });
}

async function fetchAgedBalanceTiers(
  side: AgedBalanceSide,
  asOf?: string,
  basis: AgedBalanceBasis = 'facture'
): Promise<AgedBalanceTiersResponse> {
  return apiClient.get<AgedBalanceTiersResponse>('/accounting/aged-balance/tiers', {
    asOf: asOf || undefined,
    side,
    basis,
  });
}

// ─────────────────────────────────────────────────────────────
// Queries — fournisseurs
// ─────────────────────────────────────────────────────────────

/** Liste paginée des fournisseurs. GET /api/accounting/suppliers */
export function useSuppliers(
  params: SupplierListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: supplierKeys.supplierList(params),
    queryFn: () => fetchSuppliers(params),
    enabled: options?.enabled ?? true,
  });
}

/** Détail d'un fournisseur. GET /api/accounting/suppliers/:id */
export function useSupplier(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: supplierKeys.supplierDetail(id),
    queryFn: () => fetchSupplier(id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/** Création d'un fournisseur. POST /api/accounting/suppliers */
export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SupplierInput) => createSupplier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.suppliers() });
    },
  });
}

/** Modification d'un fournisseur. PUT /api/accounting/suppliers/:id */
export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<SupplierInput> }) =>
      updateSupplier(id, data),
    onSuccess: (supplier) => {
      queryClient.setQueryData(supplierKeys.supplierDetail(supplier.id), supplier);
      queryClient.invalidateQueries({ queryKey: supplierKeys.suppliers() });
    },
  });
}

/**
 * Suppression d'un fournisseur — DÉSACTIVATION si des factures existent
 * (le serveur décide : { deleted, deactivated }).
 * DELETE /api/accounting/suppliers/:id
 */
export function useDeleteSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.suppliers() });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Queries — factures fournisseurs
// ─────────────────────────────────────────────────────────────

/** Liste paginée des factures fournisseurs. GET /api/accounting/supplier-invoices */
export function useSupplierInvoices(
  params: SupplierInvoiceListParams = {},
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: supplierKeys.invoiceList(params),
    queryFn: () => fetchSupplierInvoices(params),
    enabled: options?.enabled ?? true,
  });
}

/** Détail (lignes, fournisseur, règlements). GET /api/accounting/supplier-invoices/:id */
export function useSupplierInvoice(id: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: supplierKeys.invoiceDetail(id),
    queryFn: () => fetchSupplierInvoice(id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/** Création d'un brouillon. POST /api/accounting/supplier-invoices */
export function useCreateSupplierInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SupplierInvoiceInput) => createSupplierInvoice(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: supplierKeys.invoices() });
    },
  });
}

/** Modification d'un brouillon (remplacement intégral). PUT /api/accounting/supplier-invoices/:id */
export function useUpdateSupplierInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SupplierInvoiceInput }) =>
      updateSupplierInvoice(id, data),
    onSuccess: (_invoice, variables) => {
      queryClient.invalidateQueries({
        queryKey: supplierKeys.invoiceDetail(variables.id),
      });
      queryClient.invalidateQueries({ queryKey: supplierKeys.invoices() });
    },
  });
}

/** Suppression d'un brouillon. DELETE /api/accounting/supplier-invoices/:id */
export function useDeleteSupplierInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSupplierInvoice(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: supplierKeys.invoiceDetail(deletedId) });
      queryClient.invalidateQueries({ queryKey: supplierKeys.invoices() });
    },
  });
}

/**
 * Validation — numéro ACH-YYYY-NNNNNN, IMMUABLE, écriture au journal AC
 * via l'outbox. POST /api/accounting/supplier-invoices/:id/validate
 */
export function useValidateSupplierInvoice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => validateSupplierInvoice(id),
    onSuccess: (invoice) => {
      queryClient.setQueryData(supplierKeys.invoiceDetail(invoice.id), invoice);
      queryClient.invalidateQueries({ queryKey: supplierKeys.invoices() });
      // L'écriture AC vient d'être créée — les listes d'écritures et la
      // balance âgée fournisseurs changent.
      queryClient.invalidateQueries({ queryKey: ['accounting', 'entries'] });
      queryClient.invalidateQueries({ queryKey: [...supplierKeys.all, 'aged-balance'] });
    },
  });
}

/**
 * Règlement (garde anti-surpaiement, écriture CA/BQ + auto-lettrage 401x).
 * POST /api/accounting/supplier-invoices/:id/payments
 */
export function useRecordSupplierPayment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SupplierPaymentInput }) =>
      recordSupplierPayment(id, data),
    onSuccess: (result) => {
      queryClient.setQueryData(
        supplierKeys.invoiceDetail(result.supplierInvoice.id),
        result.supplierInvoice
      );
      queryClient.invalidateQueries({ queryKey: supplierKeys.invoices() });
      queryClient.invalidateQueries({ queryKey: ['accounting', 'entries'] });
      queryClient.invalidateQueries({ queryKey: [...supplierKeys.all, 'aged-balance'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Query — balance âgée clients/fournisseurs
// ─────────────────────────────────────────────────────────────

/**
 * Balance âgée des tiers, côté paramétrable.
 * GET /api/accounting/aged-balance?asOf=&side=CLIENT|SUPPLIER&basis=facture|echeance
 * (side=SUPPLIER exige suppliers:read en plus de accounting:read)
 */
export function useAgedBalanceSide(
  side: AgedBalanceSide,
  asOf?: string,
  options?: { enabled?: boolean; basis?: AgedBalanceBasis }
) {
  const basis = options?.basis ?? 'facture';
  return useQuery({
    queryKey: supplierKeys.agedBalance(side, asOf, basis),
    queryFn: () => fetchAgedBalanceSide(side, asOf, basis),
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Balance âgée PAR COMPTE TIERS (comptes tiers, spec 2026-07-24 §5.5) —
 * vue ADDITIVE, mêmes tranches et mêmes permissions que la vue par
 * entité. GET /api/accounting/aged-balance/tiers?asOf=&side=
 */
export function useAgedBalanceTiers(
  side: AgedBalanceSide,
  asOf?: string,
  options?: { enabled?: boolean; basis?: AgedBalanceBasis }
) {
  const basis = options?.basis ?? 'facture';
  return useQuery({
    queryKey: supplierKeys.agedBalanceTiers(side, asOf, basis),
    queryFn: () => fetchAgedBalanceTiers(side, asOf, basis),
    staleTime: 1000 * 60,
    enabled: options?.enabled ?? true,
  });
}

// ─────────────────────────────────────────────────────────────
// Exports utilitaires
// ─────────────────────────────────────────────────────────────

export {
  fetchSuppliers,
  fetchSupplier,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  fetchSupplierInvoices,
  fetchSupplierInvoice,
  createSupplierInvoice,
  updateSupplierInvoice,
  deleteSupplierInvoice,
  validateSupplierInvoice,
  recordSupplierPayment,
  fetchAgedBalanceSide,
};
